import { useState, useEffect, useMemo } from "react";
import {
    Box,
    VStack,
    HStack,
    Text,
    Badge,
    Spinner,
    useToast,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Stat,
    StatLabel,
    StatNumber,
    StatGroup,
    Tooltip,
    Code,
    Input,
    Select,
    InputGroup,
    InputLeftElement,
    IconButton,
    Button,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, TriangleUpIcon, TriangleDownIcon, SearchIcon, ChevronDownIcon, ChevronRightIcon, ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import Config from "config";

const HOST = Config.Host;

function PenaltyBadge({ penalty }) {
    const isExp = penalty.experimental;
    const isWouldMatch = penalty.isWouldMatch;
    const tooltipText = isWouldMatch
        ? `${penalty.id} - WOULD MATCH: +${penalty.minDuration || penalty.minduration || 0}ms (not enforced)`
        : `${penalty.id} - Delay: ${penalty.minDuration || penalty.minduration || 0}ms, Difficulty: ${penalty.difficulty}${isExp ? ' (EXPERIMENTAL - not enforced)' : ''}`;

    // Rainbow gradient border for wouldMatch penalties
    const rainbowStyle = isWouldMatch ? {
        background: "linear-gradient(white, white) padding-box, linear-gradient(90deg, #ff0000, #ff8000, #ffff00, #00ff00, #0080ff, #8000ff, #ff0080) border-box",
        border: "2px solid transparent",
        borderRadius: "4px",
    } : {};

    return (
        <Tooltip label={tooltipText} placement="top">
            <Badge
                colorScheme={isWouldMatch ? "gray" : "blue"}
                fontSize="xs"
                mr={1}
                mb={1}
                border={!isWouldMatch && isExp ? "2px solid" : !isWouldMatch ? "none" : undefined}
                borderColor={!isWouldMatch && isExp ? "red.500" : "transparent"}
                opacity={isWouldMatch ? 0.8 : isExp ? 0.7 : 1}
                style={rainbowStyle}
            >
                {penalty.message}
            </Badge>
        </Tooltip>
    );
}

function formatTime(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("de-AT", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
}

function formatDate(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleDateString("de-AT", {
        day: "2-digit",
        month: "2-digit",
    });
}

function SortableHeader({ label, sortKey, currentSort, onSort }) {
    const isActive = currentSort.key === sortKey;
    return (
        <Th
            cursor="pointer"
            onClick={() => onSort(sortKey)}
            _hover={{ bg: "gray.100" }}
            userSelect="none"
        >
            <HStack spacing={1}>
                <Text>{label}</Text>
                {isActive && (
                    currentSort.direction === "asc" ? (
                        <TriangleUpIcon boxSize={3} />
                    ) : (
                        <TriangleDownIcon boxSize={3} />
                    )
                )}
            </HStack>
        </Th>
    );
}

function CallRow({ call, showCustomer = false, onCustomerClick, baseZeros = 3, enforcesZeros = false }) {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <>
            <Tr bg={call.used ? "transparent" : "red.50"}>
                <Td>
                    <IconButton
                        icon={isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                        size="xs"
                        variant="ghost"
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-label="Toggle raw JSON"
                    />
                </Td>
                <Td>
                    <Tooltip label={call.createdAt}>
                        <Text fontSize="sm">
                            {formatDate(call.createdAt)}{" "}
                            {formatTime(call.createdAt)}
                        </Text>
                    </Tooltip>
                </Td>
                <Td>
                    {call.used ? (
                        <Badge colorScheme="green">
                            <CheckIcon mr={1} />
                            OK
                        </Badge>
                    ) : (
                        <Badge colorScheme="red">
                            <CloseIcon mr={1} />
                            BLOCKED
                        </Badge>
                    )}
                </Td>
                <Td>
                    {(() => {
                        // Sum enforced penalties
                        const expectedMs = call.penalties?.reduce((sum, p) => {
                            return sum + (p.minDuration || p.minduration || 0);
                        }, 0) || 0;
                        // Sum wouldMatch penalties (experimental that weren't enforced)
                        const wouldMatchMs = call.wouldMatchPenalties?.reduce((sum, p) => {
                            return sum + (p.minDuration || p.minduration || 0);
                        }, 0) || 0;
                        // Max = expected + wouldMatch (if all penalties were enforced)
                        const maxMs = expectedMs + wouldMatchMs;
                        const actualMs = call.duration || 0;
                        const actual = actualMs > 0 ? `${(actualMs / 1000).toFixed(1)}s` : "-";
                        const expected = expectedMs > 0 ? `${(expectedMs / 1000).toFixed(1)}s` : "0s";
                        const max = maxMs > 0 ? `${(maxMs / 1000).toFixed(1)}s` : "0s";
                        const isFast = actualMs > 0 && actualMs < expectedMs;
                        const hasWouldMatch = wouldMatchMs > 0;
                        return (
                            <Tooltip label={`Actual: ${actualMs}ms / Expected: ${expectedMs}ms (enforced) / Max: ${maxMs}ms (if all penalties enforced)`}>
                                <Text fontSize="sm" color={isFast ? "red.500" : "inherit"} fontWeight={isFast ? "bold" : "normal"}>
                                    {actual} / {expected}{hasWouldMatch && <Text as="span" color="gray.400"> / {max}</Text>}
                                </Text>
                            </Tooltip>
                        );
                    })()}
                </Td>
                <Td maxW="300px">
                    <Flex wrap="wrap">
                        {call.penalties?.length > 0 ? (
                            call.penalties.map((p, idx) => (
                                <PenaltyBadge
                                    key={`${call.id}-${p.id}-${idx}`}
                                    penalty={p}
                                />
                            ))
                        ) : (
                            <Text fontSize="xs" color="gray.400">
                                none
                            </Text>
                        )}
                        {/* Show wouldMatch penalties with red border */}
                        {call.wouldMatchPenalties?.length > 0 && (
                            <>
                                <Text fontSize="xs" color="gray.400" mx={1}>|</Text>
                                {call.wouldMatchPenalties.map((p, idx) => (
                                    <PenaltyBadge
                                        key={`${call.id}-wm-${p.id}-${idx}`}
                                        penalty={{ ...p, isWouldMatch: true }}
                                    />
                                ))}
                            </>
                        )}
                    </Flex>
                </Td>
                <Td>
                    {(() => {
                        const penaltyZeros = call.penalties?.reduce((sum, p) => sum + (p.zeros || 0), 0) || 0;
                        const effectiveZeros = baseZeros + penaltyZeros;
                        const hasExtra = penaltyZeros > 0;
                        return (
                            <Tooltip label={`Base: ${baseZeros} + Penalty: ${penaltyZeros} = ${effectiveZeros} zeros${enforcesZeros ? " (ENFORCED)" : " (not enforced)"}`}>
                                <Text
                                    fontSize="sm"
                                    fontWeight={hasExtra ? "bold" : "normal"}
                                    color={hasExtra ? (enforcesZeros ? "red.600" : "orange.500") : "inherit"}
                                >
                                    {effectiveZeros}
                                    {hasExtra && (
                                        <Text as="span" fontSize="xs" color="gray.500"> ({baseZeros}+{penaltyZeros})</Text>
                                    )}
                                </Text>
                            </Tooltip>
                        );
                    })()}
                </Td>
                <Td>
                    <Tooltip label={call.ipHash}>
                        <Code fontSize="xs">
                            {call.ipHash?.substring(0, 8) || "-"}
                        </Code>
                    </Tooltip>
                </Td>
                <Td>
                    <Tooltip label={call.origin}>
                        <Text fontSize="xs" maxW="150px" isTruncated>
                            {call.origin || "-"}
                        </Text>
                    </Tooltip>
                </Td>
                {showCustomer && (
                    <Td>
                        <Tooltip label={`Click to view customer ${call.customerID}`}>
                            <Code
                                fontSize="xs"
                                cursor="pointer"
                                _hover={{ bg: "blue.100", color: "blue.700" }}
                                onClick={() => onCustomerClick && onCustomerClick(call.customerID)}
                            >
                                {call.customerID?.substring(0, 8) || "-"}
                            </Code>
                        </Tooltip>
                    </Td>
                )}
            </Tr>
            {isExpanded && (
                <Tr>
                    <Td colSpan={showCustomer ? 9 : 8} bg="gray.50" p={0}>
                        <Box p={3} maxH="300px" overflowY="auto">
                            <Text fontSize="xs" fontWeight="bold" mb={2}>
                                Raw MongoDB Document:
                            </Text>
                            <Code
                                display="block"
                                whiteSpace="pre-wrap"
                                fontSize="xs"
                                p={2}
                                bg="gray.100"
                                borderRadius="md"
                                overflowX="auto"
                            >
                                {JSON.stringify(call.raw, null, 2)}
                            </Code>
                        </Box>
                    </Td>
                </Tr>
            )}
        </>
    );
}

export default function Last24hCalls({ customerID, showAllCustomers = false, onCustomerClick }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [penaltyFilter, setPenaltyFilter] = useState("");
    const [ipFilter, setIpFilter] = useState("");
    const [originFilter, setOriginFilter] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [sort, setSort] = useState({ key: "createdAt", direction: "desc" });
    const [selectedDate, setSelectedDate] = useState(""); // YYYY-MM-DD format, empty = last 24h
    const [selectedTime, setSelectedTime] = useState(""); // HH:MM format, empty = full day/24h
    const toast = useToast();

    const fetchData = (date = selectedDate, time = selectedTime) => {
        if (!customerID && !showAllCustomers) return;
        setLoading(true);

        const dateParam = date ? `&date=${date}` : "";
        const timeParam = time ? `&time=${time}` : "";
        const endpoint = showAllCustomers
            ? `${HOST}/cc/all_calls?cors=true${dateParam}${timeParam}`
            : `${HOST}/cc/customers/${customerID}/last24h_calls?cors=true${dateParam}${timeParam}`;
        fetch(endpoint, {
            credentials: "include",
        })
            .then((r) => r.json())
            .then((response) => {
                setData(response);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Failed to fetch calls:", err);
                setLoading(false);
                toast({
                    title: "Error",
                    description: "Failed to load calls",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            });
    };

    useEffect(() => {
        fetchData();
    }, [customerID, showAllCustomers]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        setSelectedTime(""); // Reset time when date changes
        fetchData(newDate, "");
    };

    const handleTimeChange = (e) => {
        const newTime = e.target.value;
        setSelectedTime(newTime);
        // If no date selected, use today
        const date = selectedDate || new Date().toISOString().split('T')[0];
        if (!selectedDate) setSelectedDate(date);
        fetchData(date, newTime);
    };

    const handleTodayClick = () => {
        setSelectedDate("");
        setSelectedTime("");
        fetchData("", "");
    };

    // Navigate time window forward/backward by 60 minutes
    const navigateTime = (direction) => {
        if (!data?.timeWindow) return;

        const currentDate = data.timeWindow.date;
        const [hours, minutes] = data.timeWindow.start.split(':').map(Number);

        // Calculate new time
        let newHours = hours + (direction * 1); // Move by 1 hour
        let newDate = currentDate;

        // Handle day rollover
        if (newHours >= 24) {
            newHours = 0;
            // Move to next day
            const d = new Date(currentDate);
            d.setDate(d.getDate() + 1);
            newDate = d.toISOString().split('T')[0];
        } else if (newHours < 0) {
            newHours = 23;
            // Move to previous day
            const d = new Date(currentDate);
            d.setDate(d.getDate() - 1);
            newDate = d.toISOString().split('T')[0];
        }

        const newTime = `${String(newHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        setSelectedDate(newDate);
        setSelectedTime(newTime);
        fetchData(newDate, newTime);
    };

    const handleSort = (key) => {
        setSort((prev) => ({
            key,
            direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
        }));
    };

    // Filter and sort calls
    const filteredCalls = useMemo(() => {
        if (!data?.calls) return [];

        let result = [...data.calls];

        // Apply status filter
        if (statusFilter === "completed") {
            result = result.filter((c) => c.used);
        } else if (statusFilter === "blocked") {
            result = result.filter((c) => !c.used);
        }

        // Apply penalty filter
        if (penaltyFilter) {
            const lowerFilter = penaltyFilter.toLowerCase();
            result = result.filter((c) =>
                c.penalties?.some(
                    (p) =>
                        p.id.toLowerCase().includes(lowerFilter) ||
                        p.message.toLowerCase().includes(lowerFilter)
                )
            );
        }

        // Apply IP filter
        if (ipFilter) {
            result = result.filter((c) =>
                c.ipHash?.toLowerCase().includes(ipFilter.toLowerCase())
            );
        }

        // Apply origin filter
        if (originFilter) {
            result = result.filter((c) =>
                c.origin?.toLowerCase().includes(originFilter.toLowerCase())
            );
        }

        // Apply customer filter (only when customer column is visible)
        if (customerFilter) {
            result = result.filter((c) =>
                c.customerID?.toLowerCase().includes(customerFilter.toLowerCase())
            );
        }

        // Sort
        result.sort((a, b) => {
            let valA, valB;

            switch (sort.key) {
                case "createdAt":
                    valA = new Date(a.createdAt).getTime();
                    valB = new Date(b.createdAt).getTime();
                    break;
                case "status":
                    valA = a.used ? 1 : 0;
                    valB = b.used ? 1 : 0;
                    break;
                case "duration":
                    valA = a.duration || 0;
                    valB = b.duration || 0;
                    break;
                case "penalties":
                    valA = a.penalties?.length || 0;
                    valB = b.penalties?.length || 0;
                    break;
                case "zeros":
                    valA = (data?.summary?.baseZeros || 3) + (a.penalties?.reduce((s, p) => s + (p.zeros || 0), 0) || 0);
                    valB = (data?.summary?.baseZeros || 3) + (b.penalties?.reduce((s, p) => s + (p.zeros || 0), 0) || 0);
                    break;
                case "ipHash":
                    valA = a.ipHash || "";
                    valB = b.ipHash || "";
                    break;
                case "origin":
                    valA = a.origin || "";
                    valB = b.origin || "";
                    break;
                case "customerID":
                    valA = a.customerID || "";
                    valB = b.customerID || "";
                    break;
                default:
                    return 0;
            }

            if (sort.direction === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            } else {
                return valA < valB ? 1 : valA > valB ? -1 : 0;
            }
        });

        return result;
    }, [data?.calls, statusFilter, penaltyFilter, ipFilter, originFilter, customerFilter, sort]);

    // Get unique penalty types for filter suggestions
    const uniquePenalties = useMemo(() => {
        if (!data?.calls) return [];
        const penalties = new Set();
        data.calls.forEach((c) => {
            c.penalties?.forEach((p) => penalties.add(p.message));
        });
        return Array.from(penalties).sort();
    }, [data?.calls]);

    // Show customer column if viewing all customers OR if this is a parent with child domains
    const showCustomerColumn = showAllCustomers || data?.isParentWithSubs;

    if (loading) {
        return (
            <Flex justify="center" align="center" h="200px">
                <Spinner size="xl" />
            </Flex>
        );
    }

    if (!data) {
        return (
            <Box p={4}>
                <Text color="red.500">Failed to load data</Text>
            </Box>
        );
    }

    return (
        <Box>
            <VStack align="stretch" spacing={4}>
                {/* Summary Stats */}
                <StatGroup>
                    <Stat>
                        <StatLabel>Total Calls</StatLabel>
                        <StatNumber>{data.summary?.total || 0}</StatNumber>
                    </Stat>
                    <Stat>
                        <StatLabel>Completed</StatLabel>
                        <StatNumber color="green.500">
                            {data.summary?.completed || 0}
                        </StatNumber>
                    </Stat>
                    <Stat>
                        <StatLabel>Blocked/Gave Up</StatLabel>
                        <StatNumber color="red.500">
                            {data.summary?.blocked || 0}
                        </StatNumber>
                    </Stat>
                    <Stat>
                        <StatLabel>Showing</StatLabel>
                        <StatNumber>{filteredCalls.length}</StatNumber>
                    </Stat>
                    <Stat>
                        <StatLabel>V2 Base Zeros</StatLabel>
                        <StatNumber>{data.summary?.baseZeros ?? "?"}</StatNumber>
                    </Stat>
                    <Stat>
                        <StatLabel>Enforces Zeros</StatLabel>
                        <StatNumber color={data.summary?.enforcesZeros ? "green.500" : "gray.400"}>
                            {data.summary?.enforcesZeros ? "YES" : "NO"}
                        </StatNumber>
                    </Stat>
                </StatGroup>

                {/* Top Penalties */}
                {data.summary?.topPenalties?.length > 0 && (
                    <Box>
                        <Text fontWeight="bold" mb={2}>
                            Top Penalties
                        </Text>
                        <HStack wrap="wrap" spacing={2}>
                            {data.summary.topPenalties.map((p) => (
                                <Badge
                                    key={p.id}
                                    colorScheme="purple"
                                    fontSize="sm"
                                    px={2}
                                    py={1}
                                    cursor="pointer"
                                    onClick={() => setPenaltyFilter(p.message)}
                                    _hover={{ opacity: 0.8 }}
                                >
                                    {p.message}: {p.count}
                                </Badge>
                            ))}
                        </HStack>
                    </Box>
                )}

                {/* Filters */}
                <HStack spacing={4} wrap="wrap">
                    <HStack spacing={2}>
                        <Input
                            type="date"
                            size="sm"
                            w="150px"
                            value={selectedDate}
                            onChange={handleDateChange}
                            max={new Date().toISOString().split('T')[0]}
                        />
                        <Input
                            type="time"
                            size="sm"
                            w="100px"
                            value={selectedTime}
                            onChange={handleTimeChange}
                            placeholder="HH:MM"
                        />
                        {data?.timeWindow && (
                            <>
                                <IconButton
                                    icon={<ArrowBackIcon />}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigateTime(-1)}
                                    aria-label="Previous hour"
                                />
                                <Badge colorScheme="blue" px={2} py={1}>
                                    {data.timeWindow.start} - {data.timeWindow.end}
                                </Badge>
                                <IconButton
                                    icon={<ArrowForwardIcon />}
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigateTime(1)}
                                    aria-label="Next hour"
                                />
                            </>
                        )}
                        <Badge
                            colorScheme={selectedDate || selectedTime ? "gray" : "green"}
                            cursor="pointer"
                            onClick={handleTodayClick}
                            px={2}
                            py={1}
                        >
                            {selectedDate || selectedTime ? "Reset to Last 24h" : "Last 24h"}
                        </Badge>
                    </HStack>
                    <Select
                        size="sm"
                        w="150px"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="completed">Completed</option>
                        <option value="blocked">Blocked</option>
                    </Select>
                    <InputGroup size="sm" w="180px">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Filter penalty..."
                            value={penaltyFilter}
                            onChange={(e) => setPenaltyFilter(e.target.value)}
                            list="penalty-suggestions"
                        />
                        <datalist id="penalty-suggestions">
                            {uniquePenalties.map((p) => (
                                <option key={p} value={p} />
                            ))}
                        </datalist>
                    </InputGroup>
                    <InputGroup size="sm" w="150px">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Filter IP..."
                            value={ipFilter}
                            onChange={(e) => setIpFilter(e.target.value)}
                        />
                    </InputGroup>
                    <InputGroup size="sm" w="180px">
                        <InputLeftElement pointerEvents="none">
                            <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                            placeholder="Filter origin..."
                            value={originFilter}
                            onChange={(e) => setOriginFilter(e.target.value)}
                        />
                    </InputGroup>
                    {showCustomerColumn && (
                        <InputGroup size="sm" w="180px">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Filter customer..."
                                value={customerFilter}
                                onChange={(e) => setCustomerFilter(e.target.value)}
                            />
                        </InputGroup>
                    )}
                </HStack>

                {/* Calls Table */}
                <Box overflowX="auto" maxH="500px" overflowY="auto">
                    <Table size="sm" variant="simple">
                        <Thead position="sticky" top={0} bg="white" zIndex={1}>
                            <Tr>
                                <Th width="40px"></Th>
                                <SortableHeader
                                    label="Time"
                                    sortKey="createdAt"
                                    currentSort={sort}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label="Status"
                                    sortKey="status"
                                    currentSort={sort}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label="Duration (actual/expected/max)"
                                    sortKey="duration"
                                    currentSort={sort}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label="Penalties"
                                    sortKey="penalties"
                                    currentSort={sort}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label="Zeros"
                                    sortKey="zeros"
                                    currentSort={sort}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label="IP Hash"
                                    sortKey="ipHash"
                                    currentSort={sort}
                                    onSort={handleSort}
                                />
                                <SortableHeader
                                    label="Origin"
                                    sortKey="origin"
                                    currentSort={sort}
                                    onSort={handleSort}
                                />
                                {showCustomerColumn && (
                                    <SortableHeader
                                        label="Customer"
                                        sortKey="customerID"
                                        currentSort={sort}
                                        onSort={handleSort}
                                    />
                                )}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {filteredCalls.length === 0 && (
                                <Tr>
                                    <Td colSpan={showCustomerColumn ? 9 : 8} textAlign="center">
                                        <Text color="gray.500">
                                            No calls match the filters
                                        </Text>
                                    </Td>
                                </Tr>
                            )}
                            {filteredCalls.map((call) => (
                                <CallRow key={call.id} call={call} showCustomer={showCustomerColumn} onCustomerClick={onCustomerClick} baseZeros={data?.summary?.baseZeros ?? 3} enforcesZeros={data?.summary?.enforcesZeros ?? false} />
                            ))}
                        </Tbody>
                    </Table>
                </Box>

                {data?.timeWindow ? (
                    <Text fontSize="sm" color="blue.500" textAlign="center">
                        Showing {data.calls?.length || 0} calls from {data.timeWindow.date} {data.timeWindow.start} to {data.timeWindow.end} (60 min window, no limit)
                    </Text>
                ) : data.calls?.length >= 500 && (
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                        Showing last 500 calls (limited for performance). Use time picker for unlimited access.
                    </Text>
                )}
            </VStack>
        </Box>
    );
}
